import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import React from 'react';

import OAuthLoginForm from './components/OAuthLoginForm';
import AuthPinShell from './AuthPinShell';

interface AuthPinCenteredLoginProps {
  t: (key: string) => string;
  error: string | null;
  success: string | null;
  redirecting?: boolean;
  selectedPlan: { planId: string; billing: string } | null;
  onBackToHome: () => void;
  onAuthStarted?: () => void;
  onAuthSuccess: () => void;
  onAuthError: (message: string) => void;
}

const AuthPinCenteredLogin: React.FC<AuthPinCenteredLoginProps> = ({
  t,
  error,
  success,
  redirecting = false,
  selectedPlan,
  onBackToHome,
  onAuthStarted,
  onAuthSuccess,
  onAuthError,
}) => {
  const goSignup = () => {
    const qs = new URLSearchParams(window.location.search).toString();
    window.location.href = qs ? `/signup?${qs}` : '/signup';
  };

  return (
    <AuthPinShell
      t={t}
      onBackToHome={onBackToHome}
      redirecting={redirecting}
      layout="form-right"
      proofMode="pin"
      eyebrowKey="auth.loginLeftEyebrow"
      titleBeforeKey="auth.loginLeftTitleBefore"
      titleAccentKey="auth.loginLeftTitleAccent"
      subtitleKey="auth.loginLeftSubtitle"
      footerStatKey="auth.pinFooterStat"
      cardHeadingKey="auth.signInWithHeading"
      cardSubtitleKey="auth.oauthSubtitle"
      cardIntroKey="auth.loginCardMicroCopy"
      formPanelTopKey="auth.loginFormPanelTop"
      formPanelBottomKey="auth.loginFormPanelBottom"
      cardFooter={
        <p className="auth-page__switch">
          {t('auth.noAccountShort')}{' '}
          <button type="button" onClick={goSignup} className="auth-page__switch-btn">
            {t('auth.signUpHere')}
          </button>
        </p>
      }
    >
      {selectedPlan && (
        <div className="auth-page__plan-banner">
          <p className="auth-page__plan-banner-label">{t('auth.selectedPlan')}</p>
          <p className="auth-page__plan-banner-value">
            {selectedPlan.planId} —{' '}
            {selectedPlan.billing === 'monthly' ? t('views.pricing.monthly') : t('views.pricing.annual')}
          </p>
        </div>
      )}

      <AnimatePresence mode="wait">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="auth-page__alert auth-page__alert--error"
            role="alert"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{error}</span>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="auth-page__alert auth-page__alert--success"
          >
            <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{success}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <OAuthLoginForm
        authMode="signin"
        onAuthStarted={onAuthStarted}
        onSuccess={onAuthSuccess}
        onError={onAuthError}
        variant="pin-light"
        oauthProviders={['google', 'linkedin']}
      />
    </AuthPinShell>
  );
};

export default AuthPinCenteredLogin;
